import React, { Suspense, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import './style.css'
import FormLayout from 'layouts/FormLayout'
import BackButton from 'components/BackButton'
import { getLoading } from 'modules/auth/AuthSelector'
import { getIsProcessingRegister } from 'modules/user/UserSelector'
import Session from 'services/SessionService'
import CookieService from 'services/CookieService'
import TrackingService from 'services/TrackingService'

import styles from './SignUp.module.sass'
import config from 'site.config'

const UniversalRegistrationV3 = React.lazy(() =>
  import('@psl-ui/dougallgpt-components').then(module => ({
    default: module.UniversalRegistrationV3
  }))
)

const SessionService = new Session()
const {
  sections,
  configuration: { envVars }
} = config

const SignUp = () => {
  const history = useHistory()
  const [open, setOpen] = useState(false)
  const [stylesLoaded, setStylesLoaded] = useState(false)
  const isProcessingRegistration = useSelector(getIsProcessingRegister)
  const isOtpLoading = useSelector(getLoading)

  const [isThnxMsg, setIsThnxMsg] = useState(false)
  const [hasFileUploader, setHasFileUploader] = useState(false)
  const [
    isRegistrationLaterOptionSelected,
    setIsRegistrationLaterOptionSelected
  ] = useState(false)

  const { email } = sections.privacy
  const { title, formURL } = sections.signup

  const [authToken, setAuthToken] = useState(
    CookieService().get('authToken')
      ? JSON.parse(CookieService().get('authToken'))
      : false
  )
  useEffect(() => {
    loadStyles()
    let timer = setInterval(() => {
      if (!authToken && CookieService().get('authToken'))
        setAuthToken(JSON.parse(CookieService().get('authToken')))
    }, 3000)
    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadStyles = async () => {
    await import('@psl-ui/dougallgpt-components/dist/style.css')
    setStylesLoaded(true)
  }

  // Language
  let languageCode = navigator.language?.split('-')[0] || 'en'
  if (navigator.language === 'en-gb') languageCode = 'en-gb'
  if (navigator.language === 'pt-BR') languageCode = 'pt-BR'
  if (
    !['en', 'en-gb', 'fr', 'it', 'de', 'ja', 'zh', 'pt-BR'].includes(
      languageCode
    )
  )
    languageCode = 'en'

  // Track initial registration form load
  useEffect(() => {
    TrackingService().track('Site/View/UReg-Email-Form')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Track when the registration form button is clicked
  useEffect(() => {
    const selector = 'button.URFv2-submitButton'
    const handler = () => {
      TrackingService().track('Site/Click/UReg-Email/Register')
    }
    const addListener = () => {
      const submitBtn = document.querySelector(selector)
      if (submitBtn && !submitBtn.dataset.listenerAdded) {
        submitBtn.addEventListener('click', handler)
        submitBtn.dataset.listenerAdded = 'true' // Prevent duplicate listeners
      }
    }
    const observer = new MutationObserver(addListener)
    observer.observe(document.body, { childList: true, subtree: true })

    addListener() // Initial call to add listener if button already exists

    return () => {
      observer.disconnect()
      const submitBtn = document.querySelector(selector)
      if (submitBtn?.dataset.listenerAdded) {
        submitBtn.removeEventListener('click', handler)
        delete submitBtn.dataset.listenerAdded
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFileUploader])

  // Track when the registration form is submitted and show thank you message
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const statusBanner = document.querySelector('.URFv2-statusBannerTitle')
      if (statusBanner) {
        const thankYouPageAction = isRegistrationLaterOptionSelected
          ? 'Site/View/Registration/Thank-You/Register-Later'
          : 'Site/View/Registration/Thank-You'
        TrackingService().track(thankYouPageAction)
        observer.disconnect()
        setIsThnxMsg(true)
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegistrationLaterOptionSelected])

  // Track when the form uploader button is in the DOM
  useEffect(() => {
    let tracked = false
    const selector = 'div.fileSelector > a.browse'
    const observer = new MutationObserver(() => {
      const fileUploader = document.querySelector(selector)
      if (fileUploader && !tracked) {
        tracked = true
        setHasFileUploader(true)
        // TrackingService().track('Site/View/UReg-UploadDoc-Form')
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // URF handlers
  const handleFormSubmit = data => {
    const isLaterRegistration =
      data?.data?.verificationType?.value.toLowerCase() === 'later'
    setIsRegistrationLaterOptionSelected(isLaterRegistration)
    const thankYouPageAction = isLaterRegistration
      ? 'Site/View/Registration/Thank-You/Register-Later'
      : 'Site/View/Registration/Thank-You'
    TrackingService().track(thankYouPageAction)
    console.log('Form submitted:', data)
  }

  const handleFormSubmitDone = () => {
    history.push('/registration-thanks')
  }

  const handleFormSubmitFail = () => {
    setOpen(true)
    TrackingService().track('Site/Click/Registration/form/submit/fail')
  }

  const onSubmitReuploadPage = () => {
    // TrackingService().track('Site/Click/UReg-UploadDoc/Submit')
  }

  const handleEmailCheckDone = result => {
    console.log('Email checked:', result)
    TrackingService().track('Site/View/Registration')
  }

  const handleEmailCheckFail = error => {
    console.error('Email check failed:', error)
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '1px solid #000',
    boxShadow: 24,
    p: 4
  }

  return (
    <FormLayout
      loading={isProcessingRegistration || isOtpLoading}
      disableLottie={true}
    >
      <div className={styles.box}>
        <h3>{title}</h3>
        <p>
          Unlock exclusive access to premier educational programs and
          opportunities tailored for your practice. By signing up, you&apos;ll
          receive:
        </p>
        <ul>
          <li>CME Tracker to view and download your CME credits</li>
          <li>
            Personalized newsletters with offers to enhance your knowledge and
            skills
          </li>
          <li>
            Invitations to participate in cutting-edge education that impacts
            patient care
          </li>
          <li>Opportunities to engage with industry experts and peers</li>
        </ul>
        <div className={styles.form}>
          {authToken.access && stylesLoaded && (
            <Suspense fallback={<div>Loading form...</div>}>
              <UniversalRegistrationV3
                config={{
                  accessToken: authToken.access,
                  formURL,
                  formID: 'universal-registration-v4',
                  registrationId: SessionService.getFromCache(
                    'registrationid',
                    ''
                  ),
                  endpointUrl: envVars.REACT_APP_URF_ENDPOINT_URL,
                  brandId: Number.parseInt(process.env.REACT_APP_AUTH_BRANDID),
                  brandCode: process.env.REACT_APP_AUTH_BRANDNAME,
                  siteId: Number.parseInt(process.env.REACT_APP_PHNX_SITEID),
                  idhSiteId: Number.parseInt(process.env.REACT_APP_IDH_SITEID),
                  languageCode,
                  interactionId: SessionService.getFromCache(
                    'interactionId',
                    ''
                  ),
                  deviceId: SessionService.getFromCache('id_key', ''),
                  lastPageVisited: SessionService.getFromCache(
                    'lastAction',
                    '/'
                  ),
                  profileId: envVars.REACT_APP_URFV3_PROFILEID,
                  recaptchaKey: envVars.REACT_APP_URFV3_RECAPTCHA_KEY,
                  recaptchaV3MinScore: 0.3,
                  profileIdv2: envVars.REACT_APP_URFV2_PROFILEID,
                  recaptchaV2Key: envVars.REACT_APP_URFV2_RECAPTCHA_KEY
                }}
                onSubmit={handleFormSubmit}
                onSubmitDone={handleFormSubmitDone}
                onSubmitFail={handleFormSubmitFail}
                onEmailCheckDone={handleEmailCheckDone}
                onEmailCheckFail={handleEmailCheckFail}
                onSubmitReuploadPage={onSubmitReuploadPage}
              />
              {!isThnxMsg && (
                <p
                  style={{ color: '#444', fontSize: '14px', marginTop: '4px' }}
                >
                  <span style={{ color: 'red' }}>
                    <b>*</b>
                  </span>{' '}
                  Required
                </p>
              )}
            </Suspense>
          )}
        </div>
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="modal-otp"
        >
          <Box sx={style} className={styles.modal}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={require('images/logo.png')} alt="logo" height="60px" />
            </div>
            <hr />
            <div>
              <p>There was an error processing your request.</p>
              <p>
                For any further assistance, do not hesitate to{' '}
                <a href={'mailto:' + email}>contact us</a>.
              </p>
            </div>
          </Box>
        </Modal>
        <BackButton />
      </div>
    </FormLayout>
  )
}

export default SignUp
